import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { SORTS } from '@sorare/marketplace/src/searchCards/football/sorts';
import { TeamModeSupportProvider } from '@sorare/marketplace/src/searchCommonPlayer/TeamModeSupport';
import {
  BackendCompetitionEligibilityGroup,
  scoutingCenterBackendMarketFilters,
} from '@sorare/marketplace/src/searchCommonPlayer/filtersGroup/football';

import { ScoutingCenterHeader } from '#src/components/scoutingCenter/ScoutingCenterHeader/index.tsx';
import { FootballPlayerSearch } from '#src/components/searchCards/FootballPlayerSearch/index.tsx';

import type {
  FootballScoutingCenter,
  FootballScoutingCenterVariables,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  min-height: calc(100vh - var(--current-stack-height));
`;

const FOOTBALL_SCOUTING_CENTER_QUERY = gql`
  query FootballScoutingCenter($slug: String!) {
    so5 {
      so5Competition(slug: $slug) {
        slug
        algoliaFilters
        scoutable
        competitions {
          slug
          ...ScoutingCenterHeader_competition
        }
        ...ScoutingCenterHeader_so5Competition
      }
    }
  }
  ${ScoutingCenterHeader.fragments.so5Competition}
  ${ScoutingCenterHeader.fragments.competition}
` as TypedDocumentNode<FootballScoutingCenter, FootballScoutingCenterVariables>;

export const ScoutingCenter = ({ slug }: { slug?: string }) => {
  const { data, loading } = useQuery(FOOTBALL_SCOUTING_CENTER_QUERY, {
    variables: { slug: slug || '' },
    skip: !slug,
  });

  const { so5Competition } = data?.so5 || {};

  const cardFilters: FiltersGroup[] = [
    ...(!slug ? [BackendCompetitionEligibilityGroup] : []),
    ...scoutingCenterBackendMarketFilters,
  ];

  const defaultFilters = useDefaultFilters({
    extraFilters: so5Competition?.algoliaFilters || undefined,
  });

  return (
    <TeamModeSupportProvider hasSo5CompetitionSlug={!!slug}>
      <Wrapper>
        <ScoutingCenterHeader
          so5Competition={so5Competition}
          competitions={so5Competition?.competitions}
          loading={loading}
        />
        <div>
          <FootballPlayerSearch
            key={so5Competition?.slug || 'DEFAULT'}
            sorts={SORTS}
            defaultSort={SORTS[0]}
            cardFilters={cardFilters}
            defaultFilters={defaultFilters}
            analyticsTags={['Scouting-center', 'Football']}
            topic={{ type: 'scoutingCenter', label: '' }}
            attributesToRetrieve={['slug']}
            shouldSkip={loading}
          />
        </div>
      </Wrapper>
    </TeamModeSupportProvider>
  );
};
